import type { ExperienceKindT } from 'server/models';

import { BODY_AREA_CATEGORY, type BodyAreaCategoryT } from './body-areas';
import { type BodyPositionT, xmlBodyPositions } from './body-positions';

import type { VideoIntentT } from './intents';

import {
  type DifficultyT,
  type LifeMomentIdT,
  type TransformationStageT,
  xmlDifficulty,
  xmlDuration,
  xmlEmotionalMechanisms,
  xmlEnvironmentNeeds,
  xmlEquipmentNeeds,
  xmlIntendedShift,
  xmlModalities,
  xmlNeurologicalMechanisms,
  xmlPhysicalMechanisms,
  xmlTargetBodyAreas,
  xmlTitle,
  xmlTransformationStages,
} from './life-moments-framework';

import { type VideoT, type XmlVideoT, xmlVideo } from './video';
import { type XmlNodeArrayT, type XmlNodeT, xmlNode } from './xml';

/** Holds all data necessary to describe a wellbeing experience. */
export type ExperienceT = {
  /** ID of the user who created this experience. */
  createdBy: string | undefined;

  /**
   * TODO: For now, `id` might be "undefined", to allow drafting experience
   * objects on the client side (and they get their `id` later, after saving
   * into the API). Later... I guess we should re-work the typing to have
   * a dedicated type for newly created experiences, not saved into the API
   * yet.
   */
  id: string | undefined;

  intent: VideoIntentT | undefined;
  isAvailable: boolean;
  kind: ExperienceKindT;
  videos: VideoT[];

  artists: string[];
  bodyAreas: string[];
  bodyPositions: BodyPositionT[];
  difficulty: DifficultyT | null | undefined;
  durationMin: number;
  emotionalMechanisms: string[];
  environmentNeeds: null | string | undefined;
  equipmentNeeds: string[];
  lifeMomentId: LifeMomentIdT | null | undefined;
  modalitiesV1: string[];
  modalitiesV2: string[];
  neurologicalMechanisms: string[];
  physicalMechanisms: string[];
  shiftFrom: string[];
  shiftTo: string[];
  title: null | string | undefined;
  transformationStages: TransformationStageT[] | undefined;
};

/**
 * Returns an array of body area categories covered by videos in the experience,
 * sorted from the most prominent to the least prominent.
 */
export function aggregateBodyAreaCategories(
  experience: ExperienceT,
): BodyAreaCategoryT[] {
  const counter: Partial<Record<BodyAreaCategoryT, number>> = {};
  for (const video of experience.videos) {
    for (const area of video.bodyAreas) {
      const category = BODY_AREA_CATEGORY[area];
      const prevCount = counter[category] ?? 0;
      counter[category] = prevCount + 1;
    }
  }
  const res = Object.keys(counter) as Array<keyof typeof counter>;
  res.sort((a, b) => (counter[b] ?? 0) - (counter[a] ?? 0));
  return res;
}

export type XmlExperienceT = XmlNodeT<'wellbeing_experience'>;

export function toXmlExperience(experience: ExperienceT): XmlExperienceT {
  const videoNodes: XmlVideoT[] = [];
  for (const video of experience.videos) {
    videoNodes.push(xmlVideo(video));
  }

  const nodes: XmlNodeArrayT = [
    xmlTitle(experience.title),
    xmlDuration(experience.durationMin),
    xmlTransformationStages(experience.transformationStages ?? []),
    xmlEmotionalMechanisms(experience.emotionalMechanisms),
    xmlEnvironmentNeeds(experience.environmentNeeds),
    xmlEquipmentNeeds(experience.equipmentNeeds),
    xmlModalities(experience.modalitiesV2),
    xmlNeurologicalMechanisms(experience.neurologicalMechanisms),
    xmlPhysicalMechanisms(experience.physicalMechanisms),
    xmlIntendedShift(experience.shiftFrom, experience.shiftTo),
    xmlTargetBodyAreas(experience.bodyAreas),
    xmlBodyPositions(experience.bodyPositions),
    xmlDifficulty(experience.difficulty),
    xmlNode(
      'video_fragments',
      videoNodes,
      'Video fragments composing this experience.',
    ),
  ];

  return xmlNode('wellbeing_experience', nodes, 'A wellbeing experience.');
}
